import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EnrollmentMode } from '@repo/shared-types';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { cn } from '@repo/ui/src/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { markChipHintLearned, useChipAffordance } from '../lib/chipAffordance';

/**
 * "Hình thức học" as a chip that explains itself — the badge cluster's anchor
 * on every attendance row (spec 2026-08-24-attendance-mode-badge-design §3).
 *
 * Neutral `outline` on purpose: ochre and red are reserved for the quota chip
 * beside it, which is a decision point. How a student pays is context, not
 * attention — the moment both chips shout, neither does.
 *
 * Same trigger pattern as `QuotaExplanationPopover`: Radix renders a real
 * `<button>` (no `asChild`, so the `Badge` span nests inside it legally), which
 * is what makes the chip keyboard-reachable and gives the screen reader
 * something to announce.
 *
 * The first line of the explanation is pulled from the `students` namespace —
 * the SAME string the enrollment form shows under this mode's radio card. It is
 * referenced, never copied: one vocabulary across the enrollment form, the Học
 * viên filter and this popover is the whole point of the feature.
 */
export function ModeExplanationPopover({
  studentName,
  mode,
  termIndex,
  termSize,
}: {
  studentName: string;
  mode: EnrollmentMode;
  /** Position of this session in the open khoá; null unless SESSION_TERM with an open term. */
  termIndex: number | null;
  /** The open khoá's size; null under the same condition as `termIndex`. */
  termSize: number | null;
}): React.ReactElement {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const affordance = useChipAffordance();

  // Bundled rather than narrowed in place: `remaining` may be zero or negative
  // (a khoá is a billing unit, not a gate — `TermState.remaining` is documented
  // unclamped), and computing it once here keeps the three copy branches below
  // reading off one number instead of re-deriving it.
  const term =
    mode === 'SESSION_TERM' && termIndex != null && termSize != null
      ? { index: termIndex, size: termSize, remaining: termSize - termIndex }
      : null;

  const badgeText = term
    ? t('attendance.modeBadge.termWithPosition', { termIndex: term.index, termSize: term.size })
    : t(`attendance.modeBadge.${mode}`);

  // Second line, khoá only. Three shapes because "còn 0 buổi" and "còn -2 buổi"
  // are not sentences a teacher should be shown; an over-run is real (the block
  // bills the overage at renewal) and gets its own wording rather than a clamp.
  let positionLine: string | null = null;
  if (term) {
    positionLine =
      term.remaining > 0
        ? t('attendance.modePopover.position', { termIndex: term.index, termSize: term.size, remaining: term.remaining })
        : term.remaining === 0
          ? t('attendance.modePopover.positionLast', { termIndex: term.index, termSize: term.size })
          : t('attendance.modePopover.positionOverrun', { termIndex: term.index, termSize: term.size, over: -term.remaining });
  } else if (mode === 'SESSION_TERM') {
    positionLine = t('attendance.modePopover.noOpenTerm');
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markChipHintLearned(); }}>
      {/* 44px tap target pinned in px — `--spacing` is 0.2rem app-wide, so a
          spacing-derived min-height lands around 35px. Same trap, same fix, as
          the quota chip right next to it. */}
      <PopoverTrigger
        aria-label={t('attendance.modeBadge.aria', { name: studentName, badge: badgeText })}
        className="inline-flex min-h-[44px] shrink-0 items-center"
      >
        <Badge variant="outline" className={cn('font-medium text-surface-600', affordance)}>
          {badgeText}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" collisionPadding={12}>
        {t(`enrollment.mode.${mode}.desc`, { ns: 'students' })}
        {positionLine && <p className="mt-1.5 text-surface-500">{positionLine}</p>}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The "Đột xuất" chip, now self-explaining like its neighbour. Same warning
 * variant it always had — ad-hoc membership IS worth a glance, unlike the
 * mode — and the same real-button trigger pattern for touch and keyboard.
 */
export function AdhocExplanationPopover({ studentName }: { studentName: string }): React.ReactElement {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const affordance = useChipAffordance();
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markChipHintLearned(); }}>
      <PopoverTrigger
        aria-label={t('attendance.adhocPopover.aria', { name: studentName })}
        className="inline-flex min-h-[44px] shrink-0 items-center"
      >
        <Badge variant="warning" className={cn('font-medium', affordance)}>
          {t('attendance.adhoc')}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" collisionPadding={12}>
        {t('attendance.adhocPopover.body')}
        <p className="mt-1.5 text-surface-500">{t('attendance.adhocPopover.billing')}</p>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The teacher-side "Đột xuất" chip — same warning treatment and real-button
 * trigger as `AdhocExplanationPopover`, for a teacher on a buổi who is NOT on
 * the slot's current teacher list (a substitute, or a trợ giảng added for
 * this session only — see `SessionTeacherRow.fromSlot`). Shares the aria
 * string with the student version: it is literally the same question, asked
 * about a different name.
 */
export function TeacherAdhocExplanationPopover({ teacherName }: { teacherName: string }): React.ReactElement {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const affordance = useChipAffordance();
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markChipHintLearned(); }}>
      <PopoverTrigger
        aria-label={t('attendance.adhocPopover.aria', { name: teacherName })}
        className="inline-flex min-h-[44px] shrink-0 items-center"
      >
        <Badge variant="warning" className={cn('font-medium', affordance)}>
          {t('attendance.adhoc')}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" collisionPadding={12}>
        {t('session.teacherAdhocPopover.body')}
        <p className="mt-1.5 text-surface-500">{t('session.teacherAdhocPopover.billing')}</p>
      </PopoverContent>
    </Popover>
  );
}
